import { ExceptionService } from '../services/exception.service';
import {
  AfterContentChecked,
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  Output,
} from '@angular/core';

import {
  AbstractControl, FormArray, UntypedFormControl,
  UntypedFormGroup, ValidatorFn, Validators
} from '@angular/forms';
import { BehaviorSubject, Observable, SubscriptionLike as ISubscription } from 'rxjs';
import {
  TogetherAuctionDIPIncomeDetails,
  TogetherAuctionDIPApplicantDetail_Request
} from './../models/api.models';
import { NodeAPIService } from './../services/node.api.service';
import { NodeAPIEntityService } from './../services/node.entity.service';
import { DipStateService } from './../services/dip-state-service.service';
import { EntityStateService } from './../services/entity-state.service';
import {
  TogetherAuctionDIPEntityType, TogetherAuctionDIPEntityState,
  TogetherAuctionDIPEntityOperation, TogetherAuctionDIPIncomeTypeBroad
} from './../models/enums';
import * as _ from 'lodash-es';

@Component({
    selector: 'together-auction-dip-income',
    templateUrl: './dip.income.component.html',
    providers: [NodeAPIService],
    standalone: false
})
export class TogetherAuctionDIPIncomeComponent implements OnInit {


  /**
   * BehaviourSubject for when employmentType is
   *  changed on touch as angular doesn't support that granularity.
   *
   * @type {BehaviorSubject<null>}
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  employmentTypeChanged: BehaviorSubject<number> = new BehaviorSubject<number>(null);

  /**
   * Setter for employment type control.
   *
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  set tadEmploymentTypeSetter(control: UntypedFormControl) {
    this.tadIncomeForm.setControl('tadEmploymentType', control);
    this.tadIncomeForm.get('tadEmploymentType').updateValueAndValidity();
    this.employmentTypeChanged.next(this.tadIncomeForm.get('tadEmploymentType').value);
  }

  /**
   * Getter for employment type.
   *
   * @readonly
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  get tadEmploymentTypeSetter() {
    return this.tadIncomeForm.get('tadEmploymentType') as UntypedFormControl;
  }

  /**
   * Setter for self employment
   *
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  set selfEmploymentTypeSetter(control: UntypedFormControl) {
    this.tadIncomeForm.setControl('tadSelfEmploymentType', control);
    // this.tadIncomeForm.get('tadSelfEmploymentType').updateValueAndValidity();
  }

  /**
   * Getter for self employment type.
   *
   * @readonly
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  get selfEmploymentTypeSetter() {
    return this.tadIncomeForm.get('tadSelfEmploymentType') as UntypedFormControl;
  }


  /**
   * Get the income label type (i.e. gross or net).
   *
   * @readonly
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  get incomeLabelType() {
    const incomeType = _.toInteger(this.tadIncomeForm.get('tadEmploymentType').value);
    return incomeType === 8 ? 'gross' : 'net';
  }

  /**
   * Label for income type as frequency differs between types.
   *
   * @readonly
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  get incomeLabel() {
    const incomeType = _.toInteger(this.tadIncomeForm.get('tadEmploymentType').value);
    return [1, 2, 4, 5, 6].indexOf(incomeType) !== -1 ? 'monthly' : 'annual';
  }

  /**
   * Variable to show income.
   *
   * @readonly
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  get showIncome() {
    return _.toInteger(this.tadIncomeForm.get('tadEmploymentType').value) !== 6;
  }

  /**
   * Validator for decimal points.
   *
   * @param {FormControl}
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  validateDecimalPoint(control: UntypedFormControl) {

    // Check the control has a decimal point.
    return _.toString(control.value).indexOf('.') !== -1 ?
      { hasDecimalPoint: { point: true } } : null;
  }

  /**
   * Employment type form control.
   *
   * @type {FormControl}
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  tadEmploymentType: UntypedFormControl = new UntypedFormControl(0, [
    Validators.min(1),
    Validators.required,
  ]);

  /**
   * Payment frequency form control.
   *
   * @type {FormControl}
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  tadEmploymentPaymentFrequency: UntypedFormControl = new UntypedFormControl(0, []);

  /**
   * Income before tax form control.
   *
   * @type {FormControl}
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  tadIncomeBeforeTax: UntypedFormControl = new UntypedFormControl(null, [
    this.validateDecimalPoint,
  ]);

  /**
   * Self employment type
   *
   * @type {FormControl}
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  tadSelfEmploymentType: UntypedFormControl = new UntypedFormControl(null, []);
  /**
   * Name of the company
   *
   * @type {FormControl}
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  tadCompanyName: UntypedFormControl = new UntypedFormControl(null, []);

  /**
   * Used for checking if this is new.
   *
   * @type {FormControl}
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  tadIsNew: UntypedFormControl = new UntypedFormControl(null, []);

  /**
   * The form for this individual income.
   *
   * @type {FormGroup}
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  tadIncomeForm: UntypedFormGroup = new UntypedFormGroup({
    tadEmploymentType: this.tadEmploymentType,
    tadEmploymentPaymentFrequency: this.tadEmploymentPaymentFrequency,
    tadIncomeBeforeTax: this.tadIncomeBeforeTax,
    tadSelfEmploymentType: this.tadSelfEmploymentType,
    tadCompanyName: this.tadCompanyName,
    tadIsNew: this.tadIsNew,
  });

  /**
   * The income object.
   *
   * @type {TogetherAuctionDIPIncomeDetails}
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  @Input() income: TogetherAuctionDIPIncomeDetails;

  /**
   *
   *
   * @type {[TogetherAuctionDIPIncomeDetails]}
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  @Input() incomes: [TogetherAuctionDIPIncomeDetails];

  /**
   * The income's iterator - to show or hide remove.
   *
   * @type {number}
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  @Input() iterator: number = 0;

  /**
   * TODO: Replace with the state service.
   *
   * @type {string}
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  @Input() incoming_case_id: string;

  /**
   * The applicant iterator - to show applicant sequence.
   *
   * @type {number}
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  @Input() applicant_iterator: number = 0;

  /**
   * The applicant this is related to.
   *
   * @type {TogetherAuctionDIPApplicantDetail_Request}
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  @Input() applicant: TogetherAuctionDIPApplicantDetail_Request =
    new TogetherAuctionDIPApplicantDetail_Request();

  /**
   * TODO: Replace with two-way binding.
   *
   * @type {EventEmitter<object>}
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  @Output() form_out: EventEmitter<object> = new EventEmitter<object>();


  /**
   * Is the income detail dialogue enabled?
   *
   * @type {boolean}
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  isIncomeDetailEnabled: boolean = true;

  /**
   * Is the delete dialog enabled?
   *
   * @type {boolean}
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  isDeleteDialogVisible: boolean = false;

  /**
   * Subscription for the state change operation.
   *
   * @type {ISubscription}
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  StateChangeOperation: ISubscription;

  /**
   * Current entity state.
   *
   * @type {TogetherAuctionDIPEntityState}
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  currentState: TogetherAuctionDIPEntityState = TogetherAuctionDIPEntityState.New;

  /**
   *
   *
   * @type {boolean}
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  isInvestmentExist: boolean = false;

  /**
   *
   *
   * @type {boolean}
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  isRentalExist: boolean = false;

  /**
   * Getter for the display name of this individual income type.
   *
   * @readonly
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  get incomeDisplayName() {
    return this.income.getIncomeDisplay(_.toInteger(this.income.EmploymentStatusId));
  }

  get isIncomeSaved() {
    return (!_.isEmpty(this.income) &&
      this.entity.isNew(this.income.Id) === false) || this.iterator > 0;
  }

  /**
   * Validator reference for the entity state service.
   *
   * @readonly
   * @type {string}
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  get validatorReference(): string {
    return 'Applicant' + this.applicant_iterator + 'Income' + this.iterator;
  }

  /**
   * Returns if the Payment Frequency needs to be displayed
   *
   * @returns {boolean}
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  get hasPaymentFrequency(): boolean {
    return _.inRange(_.toInteger(this.tadIncomeForm.get('tadEmploymentType').value), 1, 4);
  }


  /**
   * Toggles whether the delete dialog is enabled.
   *
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  toggleDeleteDialogEnabled() {
    this.isDeleteDialogVisible = !this.isDeleteDialogVisible;
  }

  /**
   * Set the entity state of this component.
   *
   * @param {TogetherAuctionDIPEntityState} state
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  setEntityState(state: TogetherAuctionDIPEntityState) {
    this.currentState = state;
    this.entity_state.setEntityState(this.validatorReference, state);
    this.tadIsNew.setValue(this.currentState !== TogetherAuctionDIPEntityState.Saved);
  }

  /**
   * Command for the income to let the parent know it has been deleted.
   *
   * @param {TogetherAuctionDIPEntityState} state
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  setSingleCastCommand(state: TogetherAuctionDIPEntityState) {
    this.currentState = state;
    this.entity_state.issueSingleCastCommand(
      this.validatorReference,
      state,
      { iterator: this.iterator, applicantId: this.applicant.Id, income: this.income }
    );
  }

  /**
   * API Call to remove income.
   *
   * @param {any} caseId
   * @param {any} applicantId
   * @param {any} incomeId
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  removeIncome(caseId, applicantId, incomeId) {
    this.setEntityState(TogetherAuctionDIPEntityState.OperationPending);
    this.entity.deleteEntities(
      TogetherAuctionDIPEntityType.EmploymentDetails,
      caseId,
      applicantId,
      incomeId,
    )
      .subscribe(
        (success) => {
          this.setEntityState(TogetherAuctionDIPEntityState.Deleted);
          this.setSingleCastCommand(TogetherAuctionDIPEntityState.Deleted);
        },
        (error) => {
          this.exception.haveReceivedError(error);
          this.setEntityState(TogetherAuctionDIPEntityState.Error);
        },
      );
  }

  /**
   * Disables the dropdown if the income is already saved.
   *
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  disableDropdownIfSaved() {
    if (!this.entity.isNew(this.income.Id)) {
      this.tadIncomeForm.get('tadEmploymentType').disable();
    }
  }

  /**
   * Save this income.
   *
   * @param {any} caseId
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  saveIncome(caseId) {
    if (_.isNil(this.income) || this.tadIncomeForm.invalid ||
      this.currentState === TogetherAuctionDIPEntityState.OperationPending) {
      return;
    }
    const entityId = this.income.Id;
    this.setEntityState(TogetherAuctionDIPEntityState.OperationPending);
    this.entity.saveEntities(
      TogetherAuctionDIPEntityType.EmploymentDetails,
      caseId,
      entityId,
      this.income)
      .subscribe(
        (success) => {
          this.income = new TogetherAuctionDIPIncomeDetails(success as Object);
          this.setEntityState(TogetherAuctionDIPEntityState.Saved);
          this.setSingleCastCommand(TogetherAuctionDIPEntityState.Saved);
          this.disableDropdownIfSaved();
          this.tadIncomeForm.markAsPristine();
        },
        (error) => {
          this.exception.haveReceivedError(error);
          this.setEntityState(TogetherAuctionDIPEntityState.Error);
          return;
        },
      );
  }


  /**
   * Populates the object from the form values.
   *
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  populateObjectFromForm() {

    // If the applicant isn't set, we dont set it on the object yet.
    if (!_.isNil(this.applicant)) {
      this.income.caseApplicantId = this.applicant.Id;
    }

    // Get the status from the object.
    const employmentStatusId = _.toInteger(this.income.employmentStatusId);

    // Populate common elements.
    this.income.CaseId = this.state.CaseId;
    this.income.employmentStatus =
      _.toInteger(this.tadIncomeForm.get('tadEmploymentType').value);
    this.income.setIncome(_.toInteger(this.tadIncomeForm.
      get('tadIncomeBeforeTax').value),
      _.toInteger(this.tadIncomeForm.get('tadEmploymentType').value));

    const paymentFrequency = this.hasPaymentFrequency ? 4 : null;
    this.income.paymentPeriodTypeId = paymentFrequency;
    this.income.setPaymentPeriodTypeId(_.toInteger(this.tadIncomeForm.
      get('tadEmploymentType').value), paymentFrequency);

    // Stuff here because, why do things consistently?
    this.income.setSubStatus(_.toInteger(this.tadIncomeForm.
      get('tadIncomeBeforeTax').value), _.toInteger(this.tadEmploymentType.value));

    if (employmentStatusId === TogetherAuctionDIPIncomeTypeBroad.SelfEmployed) {
      this.income.selfEmploymentStatus =
        this.tadIncomeForm.get('tadSelfEmploymentType').value;
      this.income.companyName = this.tadIncomeForm.get('tadCompanyName').value;
    }
  }


  /**
   * Populates the form from the object values (coming back from the API only.)
   *
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  populateFormFromObject() {

    // Set entity state so that the form doesn't emit while this is going on and spam.
    this.setEntityState(TogetherAuctionDIPEntityState.OperationPending);
    // Set the form from the incoming object values.
    const employmentStatusId = _.toInteger(this.income.employmentStatusId);
    const paymentPeriodTypeId = _.toInteger(this.income.PaymentPeriodTypeId);
    const income = this.income.getIncome(employmentStatusId);

    // Set the values.
    this.tadIncomeForm.get('tadEmploymentType').setValue(employmentStatusId);
    this.tadIncomeForm.get('tadEmploymentPaymentFrequency').setValue(paymentPeriodTypeId);
    this.tadIncomeForm.get('tadIncomeBeforeTax').setValue(income);

    // For self employed, extra fields are required.
    if (employmentStatusId === TogetherAuctionDIPIncomeTypeBroad.SelfEmployed) {
      this.tadIncomeForm.
        get('tadSelfEmploymentType').setValue(this.income.selfEmploymentStatus);
      this.tadIncomeForm.get('tadCompanyName').setValue(this.income.companyName);
    }

    // Okay, finished, emit again
    this.setEntityState(TogetherAuctionDIPEntityState.OperationIdle);
  }

  /**
   * Outputs the form to the parent component.
   *
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  handleFormOut() {
    this.form_out.emit({
      iter: this.iterator,
      form: this.tadIncomeForm,
    });
    const paymentPeriodTypeId = _.toInteger(this.income.PaymentPeriodTypeId);

    if (_.isNil(this.income.EmploymentDetails) === false &&
      _.isEmpty(this.income.EmploymentDetails) === false) {
      this.income.EmploymentDetails[0].PaymentPeriodTypeId = paymentPeriodTypeId;
    }

    if (_.isNil(this.income.SelfEmploymentDetails) === false &&
      _.isEmpty(this.income.SelfEmploymentDetails) === false) {
      this.income.SelfEmploymentDetails[0].PaymentPeriodTypeId = paymentPeriodTypeId;
    }

    if (_.isNil(this.income.OtherEmploymentTypes) === false &&
      _.isEmpty(this.income.OtherEmploymentTypes) === false) {
      this.income.OtherEmploymentTypes[0].PaymentPeriodTypeId = paymentPeriodTypeId;
    }
  }

  /**
   * Change the employment status parameter if it's full, part or self.
   *
   * @readonly
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  get idParameter() {
    const incomeType = this.income.EmploymentStatusId;
    return this.income.getDeleteId(_.toInteger(incomeType));
  }


  /**
   * Toggle the income from showing.
   *
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  toggleIncomeDetailEnabled() {
    this.isIncomeDetailEnabled = !this.isIncomeDetailEnabled;
  }

  /**
   * Delete an income specified.
   *
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  deleteIncome() {
    if (!this.entity.isNew(this.income.Id)) {
      this.removeIncome(this.state.CaseId, this.income.EmploymentStatusId, this.idParameter);
    } else {
      this.setEntityState(TogetherAuctionDIPEntityState.Deleted);
      this.setSingleCastCommand(TogetherAuctionDIPEntityState.Deleted);
    }
  }


  /**
   * Forces the income details to collapse.
   *
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  forceCollapseIncomeDetails() {

    if (this.currentState !== TogetherAuctionDIPEntityState.Saved) {
      // Forcibly set this as false, and do not change it.
      this.isIncomeDetailEnabled = false;
    }

  }

  /**
   * Force validation of the income details.
   *
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  forceValidateIncomeDetails() {

    // Gets the employment type.
    const employmentType = _.toInteger(this.tadIncomeForm.get('tadEmploymentType').value);

    // It's quicker to very specifically set the form values in this case
    // As a foreach adds overhead unnecessarily.
    if (employmentType === 3) {
      this.tadIncomeForm.get('tadSelfEmploymentType').markAsTouched();
      this.tadIncomeForm.get('tadCompanyName').markAsTouched();
    }
    if (employmentType === 0) {
      this.tadIncomeForm.get('tadEmploymentType').setErrors({ required: true });
    }

    // Mark the normal fields as untouched.
    this.tadIncomeForm.get('tadEmploymentType').markAsTouched();
    this.tadIncomeForm.get('tadIncomeBeforeTax').markAsTouched();
  }

  /**
   * Subscribe to commands coming from the parent component.
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  subscribeToParentCommands() {
    this.StateChangeOperation = this.entity_state.subscribeToStateCommand(
      this.validatorReference,
    ).subscribe(
      (command) => {
        if (command === TogetherAuctionDIPEntityOperation.Save) {
          if (this.tadIncomeForm.dirty) {
            this.saveIncome(this.state.CaseId);
          }
          return;
        } else if (command === TogetherAuctionDIPEntityOperation.Delete) {
          this.deleteIncome();
          return;
        } else if (command === TogetherAuctionDIPEntityOperation.ForceCollapse) {
          this.determineCurrentState();
          this.forceCollapseIncomeDetails();
        } else if (command === TogetherAuctionDIPEntityOperation.ForceValidate) {
          this.forceValidateIncomeDetails();
        } else {
          this.setEntityState(
            TogetherAuctionDIPEntityState.OperationIdle,
          ); // Otherwise it will pend, possibly.
          return; // Unsupported operation.
        }
      }
    );
  }

  /**
   * Toggle the unemployed stuff.
   *
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  toggleUnemployed() {

    // Get the income type
    const incomeType = _.toInteger(this.tadIncomeForm.get('tadEmploymentType').value);

    let validators: Array<ValidatorFn> = [];

    // Clear the validators from the form item on the component.
    this.tadIncomeForm.get('tadIncomeBeforeTax').clearValidators();

    // If it's not unemployed, don't add required validator.
    if (incomeType !== 6) {
      validators.push(this.validateDecimalPoint);
      validators.push(Validators.required);
      validators.push(Validators.min(3));
    } else {
      // Remove the value, we don't want to save an income for this.
      this.tadIncomeForm.get('tadIncomeBeforeTax').setValue(null);
    }

    // Set the validators on the income.
    this.tadIncomeForm.get('tadIncomeBeforeTax').setValidators(validators);

    // Update validators.
    this.tadIncomeForm.get('tadIncomeBeforeTax').updateValueAndValidity();
  }


  /**
   * Determine the current state of the income to prevent income from loading collapsed.
   *
   * @memberof TogetherAuctionDIPIncomeDetailsComponent
   */
  determineCurrentState() {
    if (_.isNil(this.income)) {
      return;
    }

    if (!this.entity.isNew(this.income.Id)) {
      this.currentState = TogetherAuctionDIPEntityState.Saved;
    }

    this.tadIsNew.setValue(this.currentState !== TogetherAuctionDIPEntityState.Saved);
  }

  /**
   * Subscribes to the form changes coming from the tad_income form.
   *
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  subscribeToFormChanges() {
    // Subscribe to observable emitted from the form.
    this.tadIncomeForm.statusChanges.subscribe((change) => {
      // Stop spam from form changes when saving.
      if (this.currentState !== TogetherAuctionDIPEntityState.OperationPending &&
        this.tadIncomeForm.enabled) {

        // Handle form out.
        this.handleFormOut();

        // Populate the API object from form values.
        this.populateObjectFromForm();
      }
    });

    this.employmentTypeChanged.subscribe((change) => {
      if (_.isNil(change)) {
        return;
      }
      // Toggle unemployed
      this.toggleUnemployed();
    });

  }

  /**
   * Implements OnInit.
   *
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  ngOnInit() {
    if (!_.isNil(this.incomes)) {
      this.isInvestmentExist = this.incomes.some(x => x.employmentStatusId === 7);
      this.isRentalExist = this.incomes.some(x => x.employmentStatusId === 8);
    }
    // Disable the form so that it doesn't bubble the change through the observable.
    this.tadIncomeForm.disable();

    // Disable dropdown if saved.
    this.disableDropdownIfSaved();

    // Populate the form from the object.
    this.populateFormFromObject();

    // Re-enable the form to allow changes to bubble.
    this.tadIncomeForm.enable();

    // Prepare unemployed.
    this.toggleUnemployed();

    // Determine state
    this.determineCurrentState();

    // Pass back to income details form.
    this.handleFormOut();
  }

  /**
   * Implements OnDestroy.
   *
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  ngOnDestroy() {
    this.StateChangeOperation.unsubscribe();
  }


  /**
   * Creates an instance of TogetherAuctionDIPIncomeComponent.
   * @param {NodeAPIService} api
   * @param {FormBuilder} fb
   * @param {NodeAPIEntityService} entity
   * @param {DipStateService} state
   * @memberof TogetherAuctionDIPIncomeComponent
   */
  constructor(
    private entity: NodeAPIEntityService,
    private state: DipStateService,
    private entity_state: EntityStateService,
    private exception: ExceptionService,
  ) {
    this.subscribeToParentCommands();
    this.subscribeToFormChanges();
  }
}
